import React, { ChangeEvent, useEffect, useMemo } from 'react';
import SearchBar from '#/components/Form/SearchBar';
import { TRootState } from '#/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  createActionSearchError,
  createActionSearchReset,
  createActionSearchSetItems,
  createActionSearchSetQuery,
  createActionSearchSetSelected,
  createActionSearchStartLoading,
  createActionSearchStopLoading,
} from '#/redux/actions/search';
import ApiManager from '#/services/api/ApiManager';
import { header2ClassName, spotifyApiConfig } from '#/utils/constants';
import useProtectedSpotify from '#/hooks/useProtectedAxios';
import { ISearchResponse } from '#/types/API/search';
import { debounce } from 'lodash';
import Header from '#/components/Form/Header/Header';
import PreviewLayout from '#/components/Layout/PreviewLayout/PreviewLayout';
import TrackListItem from '#/components/Layout/TrackListItem/TrackListItem';
import { getItemsPerBreakpoint } from '#/utils/getItemsPerBreakpoint';
import useViewport from '#/hooks/useViewport';
import ArtistPreview from '#/components/Layout/ArtistPreview/ArtistPreview';
import AlbumPreview from '#/components/Layout/AlbumPreview/AlbumPreview';
import PlaylistPreview from '#/components/Layout/PlaylistPreview/PlaylistPreview';
import { useNavigate } from 'react-router-dom';
import { ROUTE_NAMES } from '#/router/routes';
import { ISearchState, TSearchSelected } from '#/redux/reducers/search';
import Loading from '#/components/Layout/Loading/Loading';
import { urisParameter } from '#/utils/spotifyApiHelpers';
import Badge from '#/components/Form/Badge/Badge';
import TrackTableHead from '#/components/Layout/TracksTableHead/TracksTableHead';

const searchSelector = (state: TRootState) => state.search;

const Search = () => {
  const navigate = useNavigate();

  const searchSlice = useSelector(searchSelector);

  const { viewportWidth } = useViewport();

  const { artists, albums, playlists, tracks, query, selected, _loading } =
    searchSlice;

  const dispatch = useDispatch();

  const protectedSpotify = useProtectedSpotify();

  const getSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

        const responseSearch = await apiManager.getAllEntities<ISearchResponse>(
          'search',
          {
            q: query,
            type: 'album,artist,track,playlist',
          }
        );
        if ('error' in responseSearch) {
          // TODO: handle error
          dispatch(createActionSearchError(true));
        } else {
          let tracksWithFavorite: ISearchState['tracks'] = [];
          if (responseSearch.data.tracks.items.length > 0) {
            const responseFavTracks = await apiManager.getAllEntities<
              boolean[]
            >('libraryContains', {
              uris: urisParameter(
                'track',
                responseSearch.data.tracks.items.map((track) => track.id)
              ),
            });

            if ('error' in responseFavTracks) {
              //TODO: handle error
              dispatch(createActionSearchError(true));
            } else {
              tracksWithFavorite = responseSearch.data.tracks.items.map(
                (track, index) => ({
                  ...track,
                  favorite: responseFavTracks.data[index],
                })
              );
            }
          }

          dispatch(
            createActionSearchSetItems({
              albums: responseSearch.data.albums.items,
              artists: responseSearch.data.artists.items,
              playlists: responseSearch.data.playlists.items,
              tracks: tracksWithFavorite,
            })
          );
        }

        dispatch(createActionSearchStopLoading());
      }, 750),
    [dispatch, protectedSpotify]
  );

  useEffect(() => {
    if (query) {
      getSearch(query);
    } else {
      dispatch(createActionSearchReset());
    }
  }, [dispatch, getSearch, query]);

  useEffect(() => {
    return () => {
      dispatch(createActionSearchReset());
    };
  }, [dispatch]);

  const handleOnChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(createActionSearchStartLoading());
    dispatch(createActionSearchSetQuery(event.currentTarget.value));
  };

  const handleOnClear = () => dispatch(createActionSearchSetQuery(''));

  const handleClickSelected = (newSelected: TSearchSelected) =>
    dispatch(createActionSearchSetSelected(newSelected));

  return (
    <div className="relative min-h-full flex flex-col mt-theme-top-bar-negative-height">
      <div
        className={`flex flex-col z-10 w-full gap-4 py-2 px-theme-page-padding sticky top-0 bg-theme-darker-gray`}
      >
        <div className=" pl-[5rem]">
          <SearchBar
            value={query}
            onChange={handleOnChangeSearch}
            onClear={handleOnClear}
          />
        </div>
        {query && !_loading && (
          <div className="flex gap-3">
            <Badge
              text="All"
              selected={selected === 'all'}
              onClick={() => handleClickSelected('all')}
            />
            {artists.length > 0 && (
              <Badge
                text="Artists"
                selected={selected === 'artists'}
                onClick={() => handleClickSelected('artists')}
              />
            )}
            {albums.length > 0 && (
              <Badge
                text="Albums"
                selected={selected === 'albums'}
                onClick={() => handleClickSelected('albums')}
              />
            )}
            {tracks.length > 0 && (
              <Badge
                text="Songs"
                selected={selected === 'tracks'}
                onClick={() => handleClickSelected('tracks')}
              />
            )}
            {playlists.length > 0 && (
              <Badge
                text="Playlists"
                selected={selected === 'playlists'}
                onClick={() => handleClickSelected('playlists')}
              />
            )}
          </div>
        )}
      </div>
      {query ? (
        _loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loading />
          </div>
        ) : (
          <div className="p-theme-page-padding">
            {(selected === 'all' || selected === 'tracks') && (
              <div>
                {selected === 'all' ? (
                  <Header className={header2ClassName} type="h2">
                    Songs
                  </Header>
                ) : (
                  <TrackTableHead album />
                )}

                {tracks.length > 0 ? (
                  tracks.map((track, index) => {
                    if (index >= 5 && selected !== 'tracks') {
                      return;
                    }

                    return (
                      <TrackListItem
                        uri={track.uri}
                        contextUri={track.album.uri}
                        key={track.id}
                        id={track.id}
                        image={
                          track.album.images.length > 0
                            ? track.album.images[2].url
                            : ''
                        }
                        name={track.name}
                        explicit={track.explicit}
                        artists={track.artists.map((artist) => ({
                          name: artist.name,
                          id: artist.id,
                        }))}
                        followed={track.favorite}
                        duration={track.duration_ms}
                        number={selected === 'tracks' ? index + 1 : undefined}
                        album={
                          selected === 'tracks'
                            ? { name: track.album.name, id: track.album.id }
                            : undefined
                        }
                      />
                    );
                  })
                ) : (
                  <div className=" text-center">
                    <span>No data no show</span>
                  </div>
                )}
              </div>
            )}

            {(selected === 'all' || selected === 'artists') && (
              <div>
                {selected === 'all' && (
                  <Header className={header2ClassName} type="h2">
                    Artists
                  </Header>
                )}
                <PreviewLayout>
                  {artists.map((artist, index) => {
                    if (
                      index >= getItemsPerBreakpoint(viewportWidth) &&
                      selected !== 'artists'
                    ) {
                      return;
                    }

                    const handleOnClick = () =>
                      navigate(`${ROUTE_NAMES.ARTIST}/${artist.id}`);

                    return (
                      <ArtistPreview
                        uri={artist.uri}
                        key={artist.id}
                        image={
                          artist.images.length > 0 ? artist.images[0].url : ''
                        }
                        name={artist.name}
                        onClick={handleOnClick}
                      />
                    );
                  })}
                </PreviewLayout>
              </div>
            )}

            {(selected === 'all' || selected === 'albums') && (
              <div>
                {selected === 'all' && (
                  <Header className={header2ClassName} type="h2">
                    Albums
                  </Header>
                )}
                <PreviewLayout>
                  {albums.map((album, index) => {
                    if (
                      index >= getItemsPerBreakpoint(viewportWidth) &&
                      selected !== 'albums'
                    ) {
                      return;
                    }

                    const handleOnClick = () =>
                      navigate(`${ROUTE_NAMES.ALBUM}/${album.id}`);

                    return (
                      <AlbumPreview
                        uri={album.uri}
                        key={album.id}
                        image={
                          album.images.length > 0 ? album.images[0].url : ''
                        }
                        albumName={album.name}
                        artistName={album.artists[0].name}
                        onClick={handleOnClick}
                      />
                    );
                  })}
                </PreviewLayout>
              </div>
            )}

            {(selected === 'all' || selected === 'playlists') && (
              <div>
                {selected === 'all' && (
                  <Header className={header2ClassName} type="h2">
                    Playlists
                  </Header>
                )}
                <PreviewLayout>
                  {playlists.map((playlist, index) => {
                    if (
                      index >= getItemsPerBreakpoint(viewportWidth) &&
                      selected !== 'playlists'
                    ) {
                      return;
                    }

                    const handleOnClick = () =>
                      navigate(`${ROUTE_NAMES.PLAYLIST}/${playlist.id}`);

                    return (
                      <PlaylistPreview
                        uri={playlist.uri}
                        key={playlist.id}
                        image={playlist.images[0].url}
                        name={playlist.name}
                        description={playlist.description}
                        onClick={handleOnClick}
                      />
                    );
                  })}
                </PreviewLayout>
              </div>
            )}
          </div>
        )
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <span className="material-icons text-[15rem] text-theme-light-gray cursor-default">
            search
          </span>
        </div>
      )}
    </div>
  );
};

export default Search;
